import React, { useState, useEffect } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { clientService } from "../Client.service";
import { Client } from "@/types";
import { SpinnerIcon, ErrorIcon } from "@/components/icons";
import { adsService } from "@/domains/Ads/services/adsService";
import type { BudgetView } from "@/domains/Ads/types";
import {
  buildSettingsUpdate,
  formatGoogleAdsCustomerId,
  SettingsFormValues,
} from "./clientFormFields";

interface EditClientSettingsModalProps {
  isOpen: boolean;
  client: Client | null;
  onClose: () => void;
  onClientUpdated: (client: Client) => void;
}

/**
 * Client Settings form: how TouchPoint should behave for this client.
 * Operational config and toggles (posting cadence, ad campaign defaults,
 * boost strategy, auto-boost, auto-fetch ideas, the Google Ads account
 * link). Identity lives in EditClientProfileModal. All fields diff through
 * clientService.updateClient.
 */
export const EditClientSettingsModal: React.FC<EditClientSettingsModalProps> = ({
  isOpen,
  client,
  onClose,
  onClientUpdated,
}) => {
  const [postsPerWeek, setPostsPerWeek] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [serviceArea, setServiceArea] = useState("");
  const [monthlyAdBudget, setMonthlyAdBudget] = useState("");
  const [autoBoostEnabled, setAutoBoostEnabled] = useState(false);
  const [autoFetchIdeasEnabled, setAutoFetchIdeasEnabled] = useState(false);
  const [isPortfolioVisible, setIsPortfolioVisible] = useState(false);
  const [boostStrategy, setBoostStrategy] = useState("");
  const [googleAdsCustomerId, setGoogleAdsCustomerId] = useState("");
  const [googleAdsClientPays, setGoogleAdsClientPays] = useState(false);
  const [googleAdsBilledFrom, setGoogleAdsBilledFrom] = useState("");
  const [currentMonthBudget, setCurrentMonthBudget] = useState<BudgetView | null>(null);
  const [isSeedingBudget, setIsSeedingBudget] = useState(false);
  const [budgetSeedMessage, setBudgetSeedMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (client) {
      setPostsPerWeek(client.postsPerWeek?.toString() || "");
      setBusinessType(client.businessType || "");
      setServiceArea(client.serviceArea || "");
      setMonthlyAdBudget(client.monthlyAdBudgetDefault?.toString() || "");
      setAutoBoostEnabled(Boolean(client.autoBoostEnabled));
      setAutoFetchIdeasEnabled(Boolean(client.autoFetchIdeasEnabled));
      setIsPortfolioVisible(Boolean(client.isPortfolioVisible));
      setBoostStrategy(client.boostStrategy || "");
      setGoogleAdsCustomerId(formatGoogleAdsCustomerId(client.googleAdsCustomerId));
      setGoogleAdsClientPays(Boolean(client.googleAdsClientPays));
      setGoogleAdsBilledFrom(client.googleAdsBilledFrom || "");
      setError(null);
      setBudgetSeedMessage(null);
      setCurrentMonthBudget(null);
      adsService
        .getBudget(client.id)
        .then(setCurrentMonthBudget)
        .catch(() => setCurrentMonthBudget(null));
    }
  }, [client]);

  const seedCurrentMonthBudget = async () => {
    if (!client || !monthlyAdBudget) return;
    const total = parseFloat(monthlyAdBudget);
    if (!Number.isFinite(total) || total <= 0) {
      setBudgetSeedMessage("Enter a valid amount first.");
      return;
    }
    setIsSeedingBudget(true);
    setBudgetSeedMessage(null);
    try {
      const now = new Date();
      await adsService.setBudget({
        clientId: client.id,
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        totalBudget: total,
      });
      const refreshed = await adsService.getBudget(client.id);
      setCurrentMonthBudget(refreshed);
      setBudgetSeedMessage("Current month's budget row updated.");
    } catch (err) {
      setBudgetSeedMessage(
        err instanceof Error ? err.message : "Failed to update budget",
      );
    } finally {
      setIsSeedingBudget(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;

    setIsSaving(true);
    setError(null);

    try {
      const values: SettingsFormValues = {
        postsPerWeek,
        businessType,
        serviceArea,
        monthlyAdBudget,
        boostStrategy,
        autoBoostEnabled,
        autoFetchIdeasEnabled,
        isPortfolioVisible,
        googleAdsCustomerId,
        googleAdsClientPays,
        googleAdsBilledFrom,
      };
      const updateData = buildSettingsUpdate(values, client);

      if (Object.keys(updateData).length > 0) {
        await clientService.updateClient(client.id, updateData);
      }

      const updatedClient = await clientService.getClientById(client.id);
      onClientUpdated(updatedClient);
      onClose();
    } catch (err) {
      console.error("Error updating client settings:", err);
      setError(err instanceof Error ? err.message : "Failed to update settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setError(null);
      onClose();
    }
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isSaving}>
        Cancel
      </Button>
      <Button
        type="submit"
        form="edit-client-settings-form"
        disabled={isSaving}
        className="flex items-center gap-2"
      >
        {isSaving ? (
          <>
            <SpinnerIcon className="w-4 h-4" />
            Saving...
          </>
        ) : (
          "Save Changes"
        )}
      </Button>
    </>
  );

  if (!client) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Client Settings"
      size="lg"
      footer={footerContent}
    >
      <form id="edit-client-settings-form" onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Content Settings */}
        <fieldset className="space-y-4">
          <legend className="text-sm font-medium text-gray-900">Content Settings</legend>
          <div>
            <label htmlFor="settingsPostsPerWeek" className="block text-sm font-medium text-gray-700 mb-1">
              Posts per Week
            </label>
            <input
              id="settingsPostsPerWeek"
              type="number"
              value={postsPerWeek}
              onChange={(e) => setPostsPerWeek(e.target.value)}
              placeholder="e.g. 3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
              disabled={isSaving}
              min="1"
              max="30"
            />
            <p className="text-xs text-gray-500 mt-1">Used for content runway scoring</p>
          </div>

          <div className="flex items-start">
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={autoFetchIdeasEnabled}
                onChange={(e) => setAutoFetchIdeasEnabled(e.target.checked)}
                disabled={isSaving}
                className="rounded mt-0.5"
              />
              <span>
                Auto-fetch ideas enabled
                <span className="block text-xs text-gray-500">
                  Auto-generate draft ideas from saved competition profiles
                </span>
              </span>
            </label>
          </div>

          <div className="flex items-start">
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={isPortfolioVisible}
                onChange={(e) => setIsPortfolioVisible(e.target.checked)}
                disabled={isSaving}
                className="rounded mt-0.5"
              />
              <span>
                Show in portfolio
                <span className="block text-xs text-gray-500">
                  Feature this client on the public portfolio (needs 4+ public
                  media and a headline metric to appear)
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        {/* Ad Campaign Settings */}
        <fieldset className="space-y-4">
          <legend className="text-sm font-medium text-gray-900">Ad Campaign Settings</legend>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="settingsBusinessType" className="block text-sm font-medium text-gray-700 mb-1">
                Business Type
              </label>
              <select
                id="settingsBusinessType"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
              >
                <option value="">— Not set —</option>
                <option value="hospitality">Hospitality (hotels, B&Bs)</option>
                <option value="food_drink">Food & drink (restaurants, bars, cafes)</option>
                <option value="health_beauty">Health & beauty (aesthetic, dental, salons, spas)</option>
                <option value="wellness">Wellness (yoga, pilates, gyms, studios)</option>
                <option value="real_estate">Real estate</option>
                <option value="events">Events (wedding venues, event spaces)</option>
                <option value="tours_experiences">Tours & experiences</option>
                <option value="retail">Retail</option>
                <option value="e-commerce">E-commerce</option>
                <option value="services">Services</option>
                <option value="b2b">B2B</option>
                <option value="content_creator">Content creator</option>
                <option value="other">Other</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Primary driver of geographic targeting</p>
            </div>

            <div>
              <label htmlFor="settingsServiceArea" className="block text-sm font-medium text-gray-700 mb-1">
                Service Area
              </label>
              <select
                id="settingsServiceArea"
                value={serviceArea}
                onChange={(e) => setServiceArea(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
              >
                <option value="">— Not set —</option>
                <option value="local">Local</option>
                <option value="regional">Regional</option>
                <option value="national">National</option>
                <option value="online">Online</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="settingsMonthlyAdBudget" className="block text-sm font-medium text-gray-700 mb-1">
              Monthly Ad Budget (default)
            </label>
            <input
              id="settingsMonthlyAdBudget"
              type="number"
              step="0.01"
              min="0"
              value={monthlyAdBudget}
              onChange={(e) => setMonthlyAdBudget(e.target.value)}
              placeholder="e.g. 1000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
              disabled={isSaving}
            />
            <p className="text-xs text-gray-500 mt-1">
              Monthly budget in the client&apos;s currency. Saving on this form only stores
              the default for future months — use the button below to seed or update the
              current month&apos;s pool.
            </p>
          </div>

          {/* Current month's budget seeder */}
          <div className="rounded border border-gray-200 bg-gray-50 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-900">Current month&apos;s pool</div>
              <button
                type="button"
                onClick={seedCurrentMonthBudget}
                disabled={!monthlyAdBudget || isSeedingBudget || isSaving}
                className="text-xs font-medium px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isSeedingBudget
                  ? "Saving…"
                  : currentMonthBudget?.totalBudget != null
                    ? "Update this month"
                    : "Seed this month"}
              </button>
            </div>

            {currentMonthBudget?.totalBudget != null ? (
              <div className="grid grid-cols-3 gap-2 text-xs text-gray-700">
                <div>
                  <div className="text-gray-500">Total</div>
                  <div className="font-medium text-gray-900">
                    €{currentMonthBudget.totalBudget?.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Allocated</div>
                  <div className="font-medium text-gray-900">
                    €{currentMonthBudget.allocated?.toFixed(2) ?? "—"}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Remaining</div>
                  <div className="font-medium text-gray-900">
                    €{currentMonthBudget.remaining?.toFixed(2) ?? "—"}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                No budget configured for this month yet. Enter an amount above and click
                &ldquo;Seed this month&rdquo; to create the pool. Without it, boost approvals
                will fail.
              </p>
            )}

            {budgetSeedMessage && <div className="text-xs text-blue-700">{budgetSeedMessage}</div>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="settingsBoostStrategy" className="block text-sm font-medium text-gray-700 mb-1">
                Boost Strategy
              </label>
              <select
                id="settingsBoostStrategy"
                value={boostStrategy}
                onChange={(e) => setBoostStrategy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
                disabled={isSaving}
              >
                <option value="">— Not set —</option>
                <option value="conservative">Conservative</option>
                <option value="moderate">Moderate</option>
                <option value="aggressive">Aggressive (auto-execute)</option>
              </select>
            </div>

            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={autoBoostEnabled}
                  onChange={(e) => setAutoBoostEnabled(e.target.checked)}
                  disabled={isSaving}
                  className="rounded"
                />
                Auto-boost enabled
              </label>
            </div>
          </div>
        </fieldset>

        {/* Google Ads */}
        <fieldset className="space-y-4">
          <legend className="text-sm font-medium text-gray-900">Google Ads</legend>
          <div>
            <label htmlFor="settingsGoogleAdsCustomerId" className="block text-sm font-medium text-gray-700 mb-1">
              Google Ads customer ID
            </label>
            <input
              id="settingsGoogleAdsCustomerId"
              type="text"
              inputMode="numeric"
              value={googleAdsCustomerId}
              onChange={(e) => setGoogleAdsCustomerId(e.target.value)}
              placeholder="840-629-4360"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
              disabled={isSaving}
            />
            <p className="text-xs text-gray-500 mt-1">
              The client&apos;s account under the BeNerdy manager account. Its spend is synced
              nightly. Leave empty if the client has no Google Ads account.
            </p>
          </div>

          <div>
            <label htmlFor="settingsGoogleAdsBilledFrom" className="block text-sm font-medium text-gray-700 mb-1">
              Bill Google Ads from
            </label>
            <input
              id="settingsGoogleAdsBilledFrom"
              type="date"
              value={googleAdsBilledFrom}
              onChange={(e) => setGoogleAdsBilledFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors"
              disabled={isSaving}
            />
            <p className="text-xs text-gray-500 mt-1">
              Nothing before this day is billed. Empty: from the client&apos;s first job month.
            </p>
          </div>

          <div className="flex items-start">
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={googleAdsClientPays}
                onChange={(e) => setGoogleAdsClientPays(e.target.checked)}
                disabled={isSaving}
                className="rounded mt-0.5"
              />
              <span>
                Pays its own Google Ads
                <span className="block text-xs text-gray-500">
                  The client&apos;s own card pays Google, so BeNerdy fronts and rebills none of it
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <ErrorIcon className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <div className="text-red-700 text-sm">{error}</div>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
};
